import React, { useState, useEffect, useMemo } from 'react';
import {
  Box, Typography, Button, IconButton, Dialog, DialogTitle, DialogContent,
  DialogActions, TextField, CircularProgress, Snackbar, Alert, Chip
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import EmailIcon from '@mui/icons-material/Email';
import PhoneIcon from '@mui/icons-material/Phone';
import api from '../services/api';
import Layout from '../components/Layout';
import ExpandableTable from '../components/ExpandableTable';

const NAVY = '#1a237e';

export default function Customers() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [dialog, setDialog] = useState({ open: false, mode: 'add', form: null });
  const [snack, setSnack] = useState({ open: false, message: '', severity: 'success' });

  const fetchRows = async () => {
    setLoading(true);
    try {
      const r = await api.get('/customers');
      setRows(r.data || []);
    } catch (err) {
      setSnack({ open: true, message: 'Failed to load customers', severity: 'error' });
    }
    setLoading(false);
  };
  useEffect(() => { fetchRows(); }, []);

  const openAdd = () => setDialog({ open: true, mode: 'add', form: { name: '', contact_name: '', email: '', phone: '', street_address: '', city: '', state: '', zip: '', notes: '' } });
  const openEdit = (c) => setDialog({ open: true, mode: 'edit', form: { ...c } });
  const close = () => setDialog({ open: false, mode: 'add', form: null });
  const setF = (k, v) => setDialog(d => ({ ...d, form: { ...d.form, [k]: v } }));

  const save = async () => {
    const f = dialog.form;
    if (!f.name || !f.name.trim()) { setSnack({ open: true, message: 'Customer Name is required', severity: 'error' }); return; }
    if (!f.email || !f.email.trim()) { setSnack({ open: true, message: 'Email is required', severity: 'error' }); return; }
    if (!f.street_address || !f.street_address.trim()) { setSnack({ open: true, message: 'Street Address is required', severity: 'error' }); return; }
    try {
      if (dialog.mode === 'add') {
        await api.post('/customers', f);
        setSnack({ open: true, message: 'Customer added', severity: 'success' });
      } else {
        await api.put('/customers/' + f.id, f);
        setSnack({ open: true, message: 'Customer updated', severity: 'success' });
      }
      close(); fetchRows();
    } catch (err) {
      setSnack({ open: true, message: err.response?.data?.error || 'Save failed', severity: 'error' });
    }
  };

  const remove = async (c) => {
    if (!window.confirm('Delete customer "' + c.name + '"? This cannot be undone.')) return;
    try {
      await api.delete('/customers/' + c.id);
      setSnack({ open: true, message: 'Customer deleted', severity: 'success' });
      fetchRows();
    } catch (err) {
      setSnack({ open: true, message: err.response?.data?.error || 'Delete failed', severity: 'error' });
    }
  };

  const columns = useMemo(() => [
    { key: 'name', label: 'Name', render: (r) => <Box><Typography sx={{ fontWeight: 600, color: NAVY }}>{r.name}</Typography>{r.contact_name && <Typography sx={{ fontSize: 12, color: '#888' }}>{r.contact_name}</Typography>}</Box> },
    { key: 'email', label: 'Email', render: (r) => r.email ? <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, color: '#555' }}><EmailIcon sx={{ fontSize: 14 }} />{r.email}</Box> : '—' },
    { key: 'phone', label: 'Phone', render: (r) => r.phone ? <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, color: '#555' }}><PhoneIcon sx={{ fontSize: 14 }} />{r.phone}</Box> : '—' },
    { key: 'address', label: 'Address', render: (r) => <Typography sx={{ color: '#666', fontSize: 13 }}>{r.address || '—'}</Typography> },
    { key: 'contract_count', label: 'Contracts', width: 110, align: 'center',
      render: (r) => <Chip size="small" label={r.contract_count || 0} sx={{ bgcolor: '#e8eaf6', color: NAVY, fontWeight: 700 }} /> },
  ], []);

  const renderExpanded = (c) => (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5, py: 1 }}>
      {c.notes && <Typography sx={{ fontSize: 13, color: '#555' }}><strong>Notes:</strong> {c.notes}</Typography>}
      <Box sx={{ display: 'flex', gap: 1 }}>
        <Button size="small" variant="outlined" startIcon={<EditIcon />} onClick={() => openEdit(c)} sx={{ borderRadius: 2 }}>Edit</Button>
        <Button size="small" variant="outlined" color="error" startIcon={<DeleteIcon />} onClick={() => remove(c)} sx={{ borderRadius: 2 }}>Delete</Button>
      </Box>
    </Box>
  );

  return (
    <Layout>
      <Box sx={{ p: 3 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
          <Box>
            <Typography variant="h4" sx={{ fontWeight: 800, color: NAVY }}>My Customers</Typography>
            <Typography sx={{ color: '#666', mt: 0.5 }}>{rows.length} customer{rows.length !== 1 ? 's' : ''}</Typography>
          </Box>
          <Button variant="contained" startIcon={<AddIcon />} onClick={openAdd}
            sx={{ bgcolor: NAVY, borderRadius: 2, fontWeight: 700 }}>
            Add Customer
          </Button>
        </Box>

        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}><CircularProgress /></Box>
        ) : (
          <ExpandableTable
            rows={rows}
            columns={columns}
            renderExpanded={renderExpanded}
            searchPlaceholder="Search by name, email, address..."
            emptyMessage="No customers yet. Add your first one to get started."
          />
        )}

        <Dialog open={dialog.open} onClose={close} fullWidth maxWidth="sm">
          <DialogTitle sx={{ bgcolor: NAVY, color: 'white', fontWeight: 700 }}>
            {dialog.mode === 'add' ? 'Add Customer' : 'Edit Customer'}
          </DialogTitle>
          <DialogContent sx={{ pt: 3, display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
            <TextField autoFocus label="Customer Name *" placeholder="Individual or company name"
              value={dialog.form?.name || ''} onChange={(e) => setF('name', e.target.value)} fullWidth />
            <TextField label="Contact Name" placeholder="Person to contact (if customer is a company)"
              value={dialog.form?.contact_name || ''} onChange={(e) => setF('contact_name', e.target.value)} fullWidth />
            <Box sx={{ display: 'flex', gap: 2 }}>
              <TextField required label="Email *" type="email" value={dialog.form?.email || ''}
                onChange={(e) => setF('email', e.target.value)} fullWidth />
              <TextField label="Phone" value={dialog.form?.phone || ''}
                onChange={(e) => setF('phone', e.target.value)} fullWidth />
            </Box>
            <TextField required label="Street Address *" value={dialog.form?.street_address || ''}
              onChange={(e) => setF('street_address', e.target.value)} fullWidth />
            <Box sx={{ display: 'flex', gap: 2 }}>
              <TextField label="City" value={dialog.form?.city || ''}
                onChange={(e) => setF('city', e.target.value)} fullWidth />
              <TextField label="State" value={dialog.form?.state || ''}
                onChange={(e) => setF('state', e.target.value)} sx={{ width: 100 }} />
              <TextField label="ZIP" value={dialog.form?.zip || ''}
                onChange={(e) => setF('zip', e.target.value)} sx={{ width: 120 }} />
            </Box>
            <TextField label="Notes" value={dialog.form?.notes || ''}
              onChange={(e) => setF('notes', e.target.value)} fullWidth multiline rows={2} />
          </DialogContent>
          <DialogActions sx={{ p: 2 }}>
            <Button onClick={close}>Cancel</Button>
            <Button variant="contained" onClick={save} sx={{ bgcolor: NAVY, borderRadius: 2 }}>
              {dialog.mode === 'add' ? 'Add' : 'Save'}
            </Button>
          </DialogActions>
        </Dialog>

        <Snackbar open={snack.open} autoHideDuration={4000} onClose={() => setSnack(s => ({ ...s, open: false }))}>
          <Alert severity={snack.severity} onClose={() => setSnack(s => ({ ...s, open: false }))}>{snack.message}</Alert>
        </Snackbar>
      </Box>
    </Layout>
  );
}
